<template>
    <CardBase ref="card" :app="app" :card="card" @update:loading="handleLoading">

        <template #title>
            <slot name="title"></slot>
        </template>

        <template #content>
            <div class="card-context__content">

                <transition name="flyout-transition-left">
                    <div 
                        v-if="$slots.context && !loading && mounted" 
                        class="card-context__content-context">
                        <slot name="context"></slot>
                    </div>
                </transition>

                <transition name="flyout-transition-left">
                    <div 
                        v-if="$slots.flyout && !loading && mounted" 
                        class="card-context__content-flyout">
                        <slot name="flyout"></slot>
                    </div>
                </transition>

                <transition name="flyout-transition-right">
                    <div 
                        v-if="$slots.layer && !loading && mounted" 
                        class="card-context__content-layer">
                        <slot name="layer"></slot>
                    </div>
                </transition>
                
                <div class="card-context__content-main">
                    <transition name="canvas-transition">
                        <template v-if="isSubstituted">
                            <slot name="substitute" />
                        </template> 
                        <template v-else>
                            <slot name="main" />
                        </template> 
                    </transition>
                </div>

            </div>
        </template>

        <template #actions>
            <slot name="actions"></slot>
        </template>

    </CardBase>
</template>

<script>
import { mapGetters } from '@generio-ai/library-legacy/plugins/vuex'
import CardBase from '@/components/flow/cards/CardBase.vue'

export default {
    name: 'CardContext',
    components: {
        CardBase
    },
    props: {
        app: {
            type: String,
            required: true
        },
        card: {
			type: Object,
			required: true
		},
        substitutable: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            loading: false,
            mounted: false
        }
    },
    computed: {
        ...mapGetters('flows', [
            'isActiveByIndex'
        ]),

        isActive() {
            return this.mounted && this.isActiveCard
        },
        isActiveCard() {
            return this.isActiveByIndex(this.app, this.card?.index)
        },
        isSubstituted() {
            if (!this.isActive && this.substitutable) return true
            return false
        }
    },
    watch: {
        isSubstituted(newValue, oldValue) {
            if (newValue === oldValue) return
            this.$emit('substitute', newValue)
        }
    },
    emits: ['substitute', 'update:loading'],
    methods: {
        handleLoading(loading) {
            this.loading = loading
            this.$emit('update:loading', loading)
        },
        reset() {
            this.$refs.card?.reset()
        }
    },
    mounted() {
        this.mounted = true
    }
}
</script>

<style>
.card-context__content .v-btn {
    background-color: rgb(var(--v-theme-darkground));
    min-height: 50px;
}

 @media (max-width: 600px) {
    .card-context__content .v-btn {
        min-height: 40px;
        min-width: 55px;
        padding-left: 12px !important;
        padding-right: 12px !important;
    }
}
</style>

<style scoped>
.flyout-transition-left-enter-active,
.flyout-transition-left-leave-active {
    transition: all 0.4s ease;
}

.flyout-transition-left-enter-from,
.flyout-transition-left-leave-to {
    opacity: 0;
    transform: translateX(-50px);
}

.flyout-transition-left-enter-to,
.flyout-transition-left-leave-from {
    opacity: 1;
    transform: translateX(0);
}

.flyout-transition-right-enter-active,
.flyout-transition-right-leave-active {
    transition: all 0.4s ease;
}

.flyout-transition-right-enter-from,
.flyout-transition-right-leave-to {
    opacity: 0;
    transform: translateX(50px);
}

.flyout-transition-right-enter-to,
.flyout-transition-right-leave-from {
    opacity: 1;
    transform: translateX(0);
}

.canvas-transition-enter-active,
.canvas-transition-leave-active {
    transition: all 0.4s ease;
}

.canvas-transition-enter-from,
.canvas-transition-leave-to {
    opacity: 0;
}

.canvas-transition-enter-to,
.canvas-transition-leave-from {
    opacity: 1;
}

.card-context__content {
    display: block;
    position: relative;
    height: 100%;
    width: 100%;
}

.card-context__content-context {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    max-width: 74px;
    min-width: 74px;
    padding: 5px;
    padding-top: 0px;
    padding-bottom: 0px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 5px;
    background-color: rgba(var(--v-theme-darkerbase), 0.5);
    z-index: 400;
}

.card-context__content-flyout {
    position: absolute;
    top: 0;
    left: 74px;
    height: 100%;
    max-width: 74px;
    min-width: 74px;
    padding: 5px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 10px;
    background: linear-gradient(
        to right,
        rgba(var(--v-theme-background), 0.3),
        rgba(var(--v-theme-background), 0.3)
    );
    z-index: 399;
}

.card-context__content-layer {
    position: absolute;
    top: 0;
    right: 0;
    height: 100%;
    max-width: 74px;
    min-width: 74px;
    padding: 5px;
    padding-top: 0px;
    padding-bottom: 0px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    min-height: 0;
    overflow: hidden;
    gap: 5px;
    background-color: rgba(var(--v-theme-darkerbase), 0.5);
    z-index: 400;
}

.card-context__content-main {
    width: 100%;
    height: 100%;	
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
}

@media (max-width: 600px) {
    .flyout-transition-left-enter-active,
    .flyout-transition-left-leave-active {
        transition: all 0.4s ease;
    }

    .flyout-transition-left-enter-from,
    .flyout-transition-left-leave-to {
        opacity: 0;
        transform: translateX(0px) translateY(-50px);
    }

    .flyout-transition-left-enter-to,
    .flyout-transition-left-leave-from {
        opacity: 1;
        transform: translateX(0px) translateY(0px);
    }

    .card-context__content-context {
        width: 100%;
        max-width: unset;
        min-width: unset;
        height: unset;
        flex-direction: row;
        gap: 5px;
        padding: 5px;
    }

    .card-context__content-layer {
        /* TODO adjust to mobile as well */
        display: none; 
    }

    .card-context__content-flyout {
        width: 100%;
        max-width: unset;
        min-width: unset;
        height: unset;
        flex-direction: row;
        gap: 5px;
        padding: 5px;
        top: 50px;
        left: 0px;
    }

    .card-context__content-main {
        justify-content: flex-end;
    }
}
</style>